/**
 * Persist the generation wizard's state across a navigation away and back — specifically when a user
 * jumps from the "Select formats" step to build a brand-new format in the editor, then returns. Without
 * this, the wizard is pure component state and everything (uploaded work orders, selections, the step
 * they were on) is lost the moment they leave /new. Scoped to the tab (sessionStorage) and versioned so
 * a shape change can't deserialize stale data.
 */
import type { OutputFormat, SelectedFormat, WorkOrder } from "./types";

const KEY = "prisma.wizard.v1";

export interface PersistedWizard {
  step: number;
  workOrders: WorkOrder[];
  formats: SelectedFormat[];
  outputFormats: OutputFormat[];
  runName: string;
}

export function loadWizard(): PersistedWizard | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedWizard;
    if (parsed && Array.isArray(parsed.workOrders) && Array.isArray(parsed.formats)) {
      return parsed;
    }
  } catch {
    /* corrupt or unavailable storage — start fresh */
  }
  return null;
}

export function saveWizard(state: PersistedWizard): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — in-memory state still drives this session */
  }
}

export function clearWizard(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
